import { useCallback, useEffect, useRef, useState } from "react";

import { useApi } from "@platform/hooks/useApi";
import { useSse } from "@platform/hooks/useSse";

import type { NotificationItem, UnreadCountResponse } from "../types";

interface UseNotificationsReturn {
  notifications: NotificationItem[];
  loading: boolean;
  error: string | null;
  reload: () => Promise<void>;
  markRead: (id: number) => Promise<void>;
  markAllRead: () => Promise<void>;
}

/**
 * useNotifications — fetches the current user's notifications and exposes
 * mutation helpers (`markRead`, `markAllRead`). Each successful mutation
 * reloads the list so the UI stays consistent.
 *
 * Uses the shared `useApi()` hook for all HTTP calls, matching the pattern
 * in `useResources.ts` (`api` is intentionally excluded from callback
 * dependency arrays because `useApi()` returns a new object every render
 * while its methods are internally stable).
 */
export function useNotifications(): UseNotificationsReturn {
  const api = useApi();
  const [notifications, setNotifications] = useState<NotificationItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await api.get<NotificationItem[]>(
        "/api/v1/notifications",
        {
          errorMessage: "Failed to load notifications",
          showLoading: false,
        },
      );
      setNotifications(Array.isArray(result) ? result : []);
    } catch {
      setError("Failed to load notifications.");
    } finally {
      setLoading(false);
    }
    // `api` excluded — see useProjectResources note in useResources.ts.
  }, []);

  useEffect(() => {
    void reload();
  }, [reload]);

  const markRead = useCallback(
    async (id: number): Promise<void> => {
      try {
        await api.patch(
          `/api/v1/notifications/${id}/read`,
          {},
          {
            errorMessage: "Failed to mark notification as read",
            showLoading: false,
          },
        );
        // Optimistically flip the local flag, then reload to reconcile.
        setNotifications((prev) =>
          prev.map((n) => (n.id === id ? { ...n, read: true } : n)),
        );
        void reload();
      } catch {
        // Error already surfaced by useApi error handler.
      }
    },
    // `api` excluded — see note above. api.patch is stable.
    [reload],
  );

  const markAllRead = useCallback(async (): Promise<void> => {
    try {
      await api.patch(
        "/api/v1/notifications/read-all",
        {},
        {
          errorMessage: "Failed to mark all notifications as read",
          showLoading: false,
        },
      );
      setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
      void reload();
    } catch {
      // Error already surfaced by useApi error handler.
    }
  }, [reload]);

  return { notifications, loading, error, reload, markRead, markAllRead };
}

interface UseUnreadCountReturn {
  count: number;
  reload: () => Promise<void>;
}

/**
 * useUnreadCount — fetches the current user's unread notification count.
 * Returns a `reload` so callers can refresh after marking notifications read.
 */
export function useUnreadCount(): UseUnreadCountReturn {
  const api = useApi();
  const [count, setCount] = useState(0);

  const reload = useCallback(async () => {
    try {
      const result = await api.get<UnreadCountResponse>(
        "/api/v1/notifications/unread-count",
        {
          errorMessage: "Failed to load unread notification count",
          showLoading: false,
        },
      );
      setCount(result?.count ?? 0);
    } catch {
      setCount(0);
    }
    // `api` excluded — see note above. api.get is stable.
  }, []);

  useEffect(() => {
    void reload();
  }, [reload]);

  return { count, reload };
}

/**
 * useNotificationRealtime — subscribes to `notification.new` events via
 * SSE (Server-Sent Events). When an event fires, `onNewNotification` is
 * invoked so the caller can reload the list / badge.
 */
export function useNotificationRealtime(onNewNotification: () => void): {
  connected: boolean;
} {
  const callbackRef = useRef(onNewNotification);
  callbackRef.current = onNewNotification;

  const handleMessage = useCallback((msg: { type: string; data: unknown }) => {
    if (msg.type === "notification.new") {
      callbackRef.current();
    }
  }, []);

  const { connected } = useSse("sse/notifications", handleMessage);

  return { connected };
}
